/* global window */

import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  timeFormat,
  dateFormat,
  dateFormatNoTimeZone
} from 'common/helpers/dateHelper';
import {
  currencyFormat,
  convertStringToInteger
} from 'common/helpers/numberHelper';
import { reservationClick } from 'redux/reservationAvailabilities/actions';
import { createReservationCart } from 'redux/reservationCart/actions';

import Price from './Price';

const Experience = (props) => {
  const {
    experience,
    onSearch,
    reservationDate,
    guestCount,
    reservationTime,
    reservationTypeId
  } = props;
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.setting);
  const [selectedOption, setSelectedOption] = useState(experience.options[0]);
  const [payWithLoyaltyPoints, setPayWithLoyaltyPoints] = useState(false);

  const handlePayWithLoyaltyPoints = (e) => {
    if (e.target.value === 'true') {
      setPayWithLoyaltyPoints(true);
    } else {
      setPayWithLoyaltyPoints(false);
    }
  };

  const handleCreateReservation = (e) => {
    e.preventDefault();
    const newDate = e.currentTarget.getAttribute('data-time');
    const clickProperties = {
      reservationType: experience.title,
      sku: selectedOption.sku,
      price: selectedOption.price
    };
    const newReservation = {
      guestCount: convertStringToInteger(guestCount),
      reservationTypeId,
      reservationTypeOptionId: selectedOption.id,
      reservationDate: newDate
    };
    if (payWithLoyaltyPoints) {
      newReservation.additionalInfo = {
        useLoyaltyPoints: true
      };
    }
    const successFunction = () => {
      window.location.href = '/reservation/checkout/checkout';
    };
    const clickEvent = {
      reservationType: clickProperties.reservationType,
      sku: clickProperties.sku,
      price: clickProperties.price,
      noOfGuests: convertStringToInteger(guestCount),
      date: newDate
    };
    dispatch(reservationClick(clickEvent));
    dispatch(createReservationCart(newReservation, successFunction));
  };

  const handleOptionChange = (e) => {
    const so = experience.options.find(
      (option) => option.id === e.target.value
    );
    setSelectedOption(so);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newDate = e.currentTarget.getAttribute('data-date');
    onSearch({
      reservationDate: newDate,
      reservationTime,
      guestCount,
      reservationTypeId
    });
  };

  const customer = useSelector((state) => state.customer.data);
  const pricingArray = experience.loyalty?.pricing || [];
  const currentLevel = pricingArray.find(
    (level) => level.loyaltyTierId === customer?.loyalty?.loyaltyTierId
  );

  if (experience.times.length === 0) {
    return (
      <div className="c7-experience">
        <div className="c7-experience__content">
          <div className="c7-product__variant__add-to-cart">
            No available times for {dateFormatNoTimeZone(reservationDate)}
            <br />
            <br />
            {experience.nextAvailableDates.length > 0 && (
              <>
                Next available date
                <br />
                {experience.nextAvailableDates.map((date) => (
                  <button
                    className="c7-button-alt"
                    type="submit"
                    key={date}
                    onClick={handleSearch}
                    data-date={dateFormat(date, settings, 'isoDate')}
                  >
                    {dateFormat(date, settings)}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="c7-experience">
      <div className="c7-experience__content">
        <CurrentLevel
          currentLevel={currentLevel}
          settings={settings}
          payWithLoyaltyPoints={payWithLoyaltyPoints}
          handlePayWithLoyaltyPoints={handlePayWithLoyaltyPoints}
        />
        <div className="c7-product__variant__price">
          {currentLevel && (
            <>
              <label htmlFor="payWithPointsFalse">
                <input
                  name="payWithLoyaltyPoints"
                  type="radio"
                  checked={!payWithLoyaltyPoints}
                  onChange={handlePayWithLoyaltyPoints}
                  value="false"
                  id="payWithPointsFalse"
                />
                <Price variant={selectedOption} settings={settings} />
              </label>
            </>
          )}
          {!currentLevel && (
            <Price variant={selectedOption} settings={settings} />
          )}
        </div>

        <OptionArray
          options={experience.options}
          selectedOption={selectedOption}
          handleOptionChange={handleOptionChange}
          settings={settings}
        />
        {experience.times.length > 0 && (
          <form action="" className="c7-form c7-product__variant" method="post">
            <div className="c7-product__variant__add-to-cart">
              Available times for {dateFormatNoTimeZone(reservationDate)}
              <br />
              {experience.times.map((time) => (
                <button
                  className={
                    time.isRequestedTime ? 'c7-button' : 'c7-button-alt'
                  }
                  type="submit"
                  key={time.time}
                  onClick={handleCreateReservation}
                  data-time={time.time}
                >
                  Reserve {timeFormat(time.time, settings)}
                </button>
              ))}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const CurrentLevel = (props) => {
  const {
    currentLevel,
    settings,
    payWithLoyaltyPoints,
    handlePayWithLoyaltyPoints
  } = props;
  if (!currentLevel) {
    return null;
  }
  return (
    <div className="widget-pricing-add-to-cart-with-points">
      <label htmlFor="payWithPointsTrue">
        <input
          name="payWithLoyaltyPoints"
          type="radio"
          checked={payWithLoyaltyPoints}
          onChange={handlePayWithLoyaltyPoints}
          value="true"
          id="payWithPointsTrue"
        />
        {currencyFormat(currentLevel.price, settings)} <span>+</span>{' '}
        {currentLevel.points} <span className="points">points</span>
        <span className="price-title">/ person</span>
      </label>
    </div>
  );
};

const OptionArray = (props) => {
  const { options, selectedOption, handleOptionChange, settings } = props;
  if (options.length === 1) {
    return null;
  }
  return (
    <label htmlFor="c7-reservation-option">
      Options for this experience
      <br />
      <select
        className="c7-product__variant__select"
        name="optionId"
        id="c7-reservation-option"
        value={selectedOption.id}
        onChange={handleOptionChange}
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.title} {currencyFormat(option.addToCartPrice, settings)}
          </option>
        ))}
      </select>
    </label>
  );
};

export default Experience;
